import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatExpansionPanel } from '@angular/material/expansion';
import { asapScheduler, Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { map, observeOn, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { AlertInfo, AlertService, AlertType } from 'src/app/Services/Alert/alert.service';
import { BuildingService } from 'src/app/Services/Building/building.service';
import { FloorService } from 'src/app/Services/Floor/floor.service';
import { WorkstationService } from 'src/app/Services/Workstation/workstation.service';

@Component({
  standalone: false,
  selector: 'app-create-issue',
  templateUrl: './create-issue.component.html',
  styleUrls: ['./create-issue.component.scss'],
})
export class CreateIssueComponent implements OnInit, OnDestroy {
  @ViewChild(MatExpansionPanel, { static: true }) expansionPanel: MatExpansionPanel;

  /** Subject that emits when the component has been destroyed. */
  protected _onDestroy = new Subject<void>();

  buildings: any[] = [];
  buildings$: Observable<any[]>;
  floors: any[] = [];
  workstations: any[] = [];
  workstationSearch: UntypedFormControl = new UntypedFormControl();
  workstationSearchSub: Subscription;
  filteredWorkstations: ReplaySubject<any[]> = new ReplaySubject(1);
  noResultsLabel: string = 'No Results';

  private _selectedBuildingId: number;
  get selectedBuildingId(): number {
    return this._selectedBuildingId;
  }
  set selectedBuildingId(newId: number) {
    this._selectedBuildingId = newId;
    this.selectedFloorId = null;
    if (newId) {
      this.buildingService.getFloors(newId, true).subscribe((floors) => {
        this.floors = floors.value;
      });
    }
  }
  private _selectedFloorId: number;
  get selectedFloorId(): number {
    return this._selectedFloorId;
  }
  set selectedFloorId(newId: number) {
    this._selectedFloorId = newId;
    this.selectedDeskId = null;
    if (newId) {
      this.floorService.getWorkstations(newId, true).subscribe((workstations) => {
        this.workstations = workstations.value;
        this.selectedDeskId = null;
        this.filteredWorkstations.next(this.workstations.slice());
      });
    }
  }
  selectedDeskId: number;
  issue: string = '';

  constructor(
    private buildingService: BuildingService,
    private floorService: FloorService,
    private workstationService: WorkstationService,
    private alertService: AlertService
  ) {}

  ngOnInit(): void {
    this.buildings$ = this.buildingService.getBuildings(0, 65535, true).pipe(
      observeOn(asapScheduler),
      map((data) => data?.value?.result ?? []),
      tap((buildings) => {
        this.buildings = buildings;
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    // listen for search field value changes
    this.workstationSearch.valueChanges.pipe(takeUntil(this._onDestroy)).subscribe(() => {
      this.filterDesks();
    });
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  protected filterDesks() {
    if (!this.workstations) {
      return;
    }
    // get the search keyword
    let search = this.workstationSearch.value;
    if (!search) {
      this.filteredWorkstations.next(this.workstations.slice());
      return;
    } else {
      search = search.toLowerCase();
    }
    // filter the workstations
    this.filteredWorkstations.next(
      this.workstations.filter((ws) => ws.number.toLowerCase().indexOf(search) > -1)
    );
  }

  resetPanel() {
    this.selectedBuildingId = null;
    this.selectedFloorId = null;
    this.selectedDeskId = null;
    this.issue = ' ';
    this.floors = null;
    this.workstations = null;
    this.filteredWorkstations.next(null);
  }

  reportIssue() {
    const obj = {};
    obj['workstationId'] = this.selectedDeskId ? this.selectedDeskId : 0;
    obj['issue'] = this.issue;
    this.floorService.getFloor(this.selectedFloorId).subscribe((floor) => {
      obj['isOtc'] = floor.isOTC;
    });

    window.scrollTo(0, 0);
    this.workstationService.addIssue(obj).subscribe(() => {
      this.resetPanel();
      this.expansionPanel.close();
      this.alertService.setAlert(<AlertInfo>{
        title: 'Your report has been sent',
        message: '',
        type: AlertType.Success,
        closeable: true,
      });
    });
  }
}
